
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Job, JobStatus, Expense, User, TimeEntry, ContractorInvoice, LedgerEntry, FinancialRecord } from '../types';
import { extractReceiptData } from '../services/gemini';
import { Icons } from '../constants';
import EstimateTemplate from './EstimateTemplate';
import { exportCsv } from '../lib/changeLog';
import { apiUrl } from '../api';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

interface Props {
  jobs: Job[];
  expenses: Expense[];
  onAddExpense: (expense: Expense) => void;
  onUpdateJob: (job: Job) => void;
  timeEntries: TimeEntry[];
  onUpdateTimeEntry: (entry: TimeEntry) => void;
  contractorInvoices: ContractorInvoice[];
  onUpdateContractorInvoice: (invoice: ContractorInvoice) => void;
  ledgerEntries: LedgerEntry[];
  onAddLedgerEntry: (entry: LedgerEntry) => void;
  onUpdateLedgerEntry: (entry: LedgerEntry) => void;
  financialRecords?: FinancialRecord[];
  onUpdateFinancialRecord?: (record: FinancialRecord) => void;
  onDeleteFinancialRecord?: (recordId: string) => void;
}

const AdminFinanceView: React.FC<Props> = ({ 
  jobs, 
  expenses, 
  onAddExpense, 
  onUpdateJob,
  timeEntries,
  onUpdateTimeEntry,
  contractorInvoices,
  onUpdateContractorInvoice,
  ledgerEntries,
  onAddLedgerEntry,
  onUpdateLedgerEntry,
  financialRecords,
  onUpdateFinancialRecord,
  onDeleteFinancialRecord
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [currentExpense, setCurrentExpense] = useState<Partial<Expense>>({
    merchant: '',
    amount: 0,
    category: 'Supplies',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const [currentLedgerEntry, setCurrentLedgerEntry] = useState<Partial<LedgerEntry>>({
    transactionId: `GT-${new Date().toISOString().split('T')[0].replace(/-/g, '')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
    date: new Date().toISOString().split('T')[0],
    type: 'Expense',
    description: '',
    payerPayee: '',
    category: 'Operating Expense',
    amount: 0,
    paymentMethod: 'Credit Card',
    reconciled: false
  });

  const [activeTab, setActiveTab] = useState<'overview' | 'records' | 'ledger' | 'budgeting' | 'taxes' | 'approvals'>('overview');
  const [previewJob, setPreviewJob] = useState<Job | null>(null);
  const [editingRecord, setEditingRecord] = useState<FinancialRecord | null>(null);
  const [recordToDelete, setRecordToDelete] = useState<FinancialRecord | null>(null);

  const [ledgerSync, setLedgerSync] = useState<{ lastSyncAt?: string; entriesAdded?: number; source?: string } | null>(null);
  const [isSyncingLedger, setIsSyncingLedger] = useState(false);
  const [ledgerSyncMessage, setLedgerSyncMessage] = useState('');

  useEffect(() => {
    getDoc(doc(db, 'syncStatus', 'ledger'))
      .then(snap => setLedgerSync(snap.exists() ? (snap.data() as any) : null))
      .catch(() => setLedgerSync(null));
  }, []);

  const visibleLedger = useMemo(
    () => ledgerEntries.filter(entry => !(entry as any).isDeleted),
    [ledgerEntries],
  );

  const ledgerRunning = useMemo(() => {
    const sorted = [...visibleLedger].sort((a, b) => a.date.localeCompare(b.date));
    let balance = 0;
    const byId = new Map<string, number>();
    for (const entry of sorted) {
      balance += entry.type === 'Income' ? Number(entry.amount) || 0 : -(Number(entry.amount) || 0);
      byId.set(entry.id, balance);
    }
    const income = visibleLedger.filter(e => e.type === 'Income').reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const expense = visibleLedger.filter(e => e.type === 'Expense').reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const unreconciled = visibleLedger.filter(e => !e.reconciled).length;
    return {
      balance,
      income,
      expense,
      unreconciled,
      byId,
      latestDate: sorted.length ? sorted[sorted.length - 1].date : '',
    };
  }, [visibleLedger]);

  const syncLedgerNow = async () => {
    if (isSyncingLedger) return;
    setIsSyncingLedger(true);
    setLedgerSyncMessage('');
    try {
      const res = await fetch(apiUrl('/api/gmail/sync'), { method: 'POST' });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error || `HTTP ${res.status}`);
      const state = {
        lastSyncAt: new Date().toISOString(),
        entriesAdded: Number(out.entries) || 0,
        source: 'manual',
        googleConnected: true,
      };
      await setDoc(doc(db, 'syncStatus', 'ledger'), state, { merge: true }).catch(() => {});
      setLedgerSync(state);
      setLedgerSyncMessage(`Sync complete — ${state.entriesAdded} new ledger entr${state.entriesAdded === 1 ? 'y' : 'ies'}.`);
    } catch (err: any) {
      setLedgerSyncMessage(err?.message || 'Ledger sync failed.');
    } finally {
      setIsSyncingLedger(false);
    }
  };

  const visibleRecords = (financialRecords || []).filter((r: any) => !r.isDeleted);
  const [confirmingAction, setConfirmingAction] = useState<{
    type: 'invoice' | 'invoice_paid' | 'time_entry' | 'contractor_invoice';
    status: string;
    item: any;
  } | null>(null);

  const receiptInputRef = useRef<HTMLInputElement>(null);
  
  const pendingInvoices = jobs.filter(j => j.status === JobStatus.COMPLETED || j.status === JobStatus.INVOICED);
  const totalRevenue = jobs.reduce((acc, j) => j.status === JobStatus.PAID ? acc + j.totalPrice : acc, 0);
  const outstanding = jobs.reduce((acc, j) => j.status === JobStatus.INVOICED ? acc + j.totalPrice : acc, 0);
  const totalExpenses = expenses.reduce((acc, e) => acc + e.amount, 0);

  const pendingTimeEntries = timeEntries.filter(t => t.status === 'PENDING');
  const pendingContractorInvoices = contractorInvoices.filter(i => i.status === 'PENDING');
  const approvalCount = pendingTimeEntries.length + pendingContractorInvoices.length;

  const handleReceiptUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    setIsScanning(true);
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(',')[1];
      const data = await extractReceiptData(base64);
      if (data) {
        setCurrentExpense({
          merchant: data.merchant || '',
          amount: data.amount || 0,
          category: data.category || 'Supplies',
          date: data.date || new Date().toISOString().split('T')[0],
          notes: 'Scanned Receipt',
          receiptUrl: reader.result as string
        });
        setShowExpenseModal(true);
      }
      setIsScanning(false);
      if (receiptInputRef.current) receiptInputRef.current.value = '';
    };
    reader.readAsDataURL(file);
  };

  const handleManualAdd = () => {
    setCurrentExpense({
      merchant: '',
      amount: 0,
      category: 'Supplies',
      date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowExpenseModal(true);
  };

  const handleSaveExpense = (e: React.FormEvent) => {
    e.preventDefault();
    onAddExpense({
      id: Math.random().toString(36).substr(2, 9),
      merchant: currentExpense.merchant || 'Unknown Vendor',
      amount: Number(currentExpense.amount) || 0,
      date: currentExpense.date || new Date().toISOString(),
      category: currentExpense.category || 'General',
      notes: currentExpense.notes,
      receiptUrl: currentExpense.receiptUrl
    });
    setShowExpenseModal(false);
  };

  const handleSaveLedgerEntry = (e: React.FormEvent) => {
    e.preventDefault();
    
    let isoDate = new Date().toISOString();
    if (currentLedgerEntry.date) {
      isoDate = currentLedgerEntry.date.includes('T') 
        ? currentLedgerEntry.date 
        : `${currentLedgerEntry.date}T12:00:00Z`;
    }

    const entry: LedgerEntry = {
      id: currentLedgerEntry.id || Math.random().toString(36).substr(2, 9),
      transactionId: currentLedgerEntry.transactionId || '',
      date: isoDate,
      type: currentLedgerEntry.type as any || 'Expense',
      description: currentLedgerEntry.description || '',
      payerPayee: currentLedgerEntry.payerPayee || '',
      category: currentLedgerEntry.category || '',
      amount: Number(currentLedgerEntry.amount) || 0,
      paymentMethod: currentLedgerEntry.paymentMethod || '',
      reconciled: !!currentLedgerEntry.reconciled,
      referenceUrl: currentLedgerEntry.referenceUrl || ''
    };

    if (currentLedgerEntry.id) {
      onUpdateLedgerEntry(entry);
    } else {
      onAddLedgerEntry(entry);
    }
    setShowLedgerModal(false);
  };

  const handleConfirmAction = () => {
    if (!confirmingAction) return;

    const { type, status, item } = confirmingAction;

    if (type === 'invoice') {
      onUpdateJob({ ...item, status: JobStatus.INVOICED });
      alert(`Invoice #GT-${item.id.substr(0,4)} has been generated and emailed to ${item.email}`);
    } else if (type === 'invoice_paid') {
      onUpdateJob({ ...item, status: JobStatus.PAID });
      alert(`Job marked as Paid.`);
    } else if (type === 'time_entry') {
      onUpdateTimeEntry({ ...item, status });
    } else if (type === 'contractor_invoice') {
      onUpdateContractorInvoice({ ...item, status });
    }

    setConfirmingAction(null);
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-3xl font-black text-bark">Financial Center</h2>
          <p className="text-stone-500 font-medium">Complete financial records, budgeting, and tax management.</p>
        </div>
        <div className="flex bg-white p-1 rounded-2xl border border-stone-200 shadow-sm overflow-x-auto">
          <button 
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-2 rounded-xl font-bold text-sm transition whitespace-nowrap ${activeTab === 'overview' ? 'bg-bark text-white' : 'text-stone-500 hover:bg-stone-50'}`}
          >
            Overview
          </button>
          <button 
            onClick={() => setActiveTab('records')}
            className={`px-6 py-2 rounded-xl font-bold text-sm transition whitespace-nowrap ${activeTab === 'records' ? 'bg-bark text-white' : 'text-stone-500 hover:bg-stone-50'}`}
          >
            Records
          </button>
          <button 
            onClick={() => setActiveTab('ledger')}
            className={`px-6 py-2 rounded-xl font-bold text-sm transition whitespace-nowrap ${activeTab === 'ledger' ? 'bg-bark text-white' : 'text-stone-500 hover:bg-stone-50'}`}
          >
            General Ledger
          </button>
          <button 
            onClick={() => setActiveTab('budgeting')}
            className={`px-6 py-2 rounded-xl font-bold text-sm transition whitespace-nowrap ${activeTab === 'budgeting' ? 'bg-bark text-white' : 'text-stone-500 hover:bg-stone-50'}`}
          >
            Budgeting
          </button>
          <button 
            onClick={() => setActiveTab('taxes')}
            className={`px-6 py-2 rounded-xl font-bold text-sm transition whitespace-nowrap ${activeTab === 'taxes' ? 'bg-bark text-white' : 'text-stone-500 hover:bg-stone-50'}`}
          >
            Taxes & Payroll
          </button>
          <button 
            onClick={() => setActiveTab('approvals')}
            className={`px-6 py-2 rounded-xl font-bold text-sm transition flex items-center space-x-2 whitespace-nowrap ${activeTab === 'approvals' ? 'bg-bark text-white' : 'text-stone-500 hover:bg-stone-50'}`}
          >
            <span>Approvals</span>
            {approvalCount > 0 && (
              <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full">
                {approvalCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {activeTab === 'records' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-black text-bark">Quotes & Invoices — Records</h3>
              <p className="text-stone-500 text-sm">Every quote and invoice generated by the system. Edit, delete (soft) or export to verify what's stored.</p>
            </div>
            <button
              onClick={() => exportCsv(
                `financial-records-${new Date().toISOString().slice(0, 10)}.csv`,
                ['ID', 'Type', 'Status', 'Job ID', 'Contact ID', 'Amount', 'Date', 'Due Date'],
                visibleRecords.map((r: FinancialRecord) => [r.id, r.type, r.status, r.jobId, r.contactId, r.amount, r.date?.slice(0, 10) || '', r.dueDate?.slice(0, 10) || ''])
              )}
              className="px-4 py-2 bg-stone-100 text-stone-600 rounded-lg font-bold text-sm hover:bg-stone-200 transition"
            >
              Export CSV
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">Record</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">Type</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">Status</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest text-right">Amount</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">Date</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {visibleRecords.length === 0 && (
                    <tr><td colSpan={6} className="px-6 py-16 text-center text-stone-400 font-bold italic">No quotes or invoices yet. They are created automatically when jobs are converted or invoiced.</td></tr>
                  )}
                  {visibleRecords.map((r: FinancialRecord) => (
                    <tr key={r.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-6 py-4 font-mono text-xs">#GT-{r.id.slice(0, 4)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${r.type === 'INVOICE' ? 'bg-bark/10 text-bark' : 'bg-sky-100 text-sky-700'}`}>{r.type}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${r.status === 'PAID' ? 'bg-gecko/15 text-gecko' : r.status === 'SENT' ? 'bg-orange-100 text-orange-600' : 'bg-stone-100 text-stone-500'}`}>{r.status}</span>
                      </td>
                      <td className="px-6 py-4 font-black text-palm text-right">${Number(r.amount || 0).toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm text-stone-600">{r.date ? new Date(r.date).toLocaleDateString() : '—'}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end space-x-2">
                          <button onClick={() => setEditingRecord({ ...r })} className="px-3 py-1.5 bg-stone-100 text-stone-600 text-xs font-bold rounded-lg hover:bg-stone-200 transition">Edit</button>
                          <button onClick={() => setRecordToDelete(r)} className="px-3 py-1.5 bg-red-50 text-red-600 text-xs font-bold rounded-lg hover:bg-red-100 transition">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'overview' && (
        <div className="space-y-8 animate-fade-in">
          {/* Top Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Revenue MTD</p>
          <h3 className="text-4xl font-black text-gecko mt-2">${totalRevenue.toLocaleString()}</h3>
        </div>
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Receivables</p>
          <h3 className="text-4xl font-black text-orange-500 mt-2">${outstanding.toLocaleString()}</h3>
        </div>
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">OpEx MTD</p>
          <h3 className="text-4xl font-black text-red-500 mt-2">${totalExpenses.toLocaleString()}</h3>
        </div>
        <div className="bg-bark p-8 rounded-[32px] shadow-xl text-white">
          <p className="text-[10px] font-black text-leaf uppercase tracking-widest">Profit Center</p>
          <h3 className="text-4xl font-black mt-2">${(totalRevenue - totalExpenses).toLocaleString()}</h3>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Billing Center */}
        <div className="bg-white rounded-[40px] border border-stone-200 overflow-hidden shadow-sm">
          <div className="p-8 border-b border-stone-100 flex justify-between items-center">
            <h4 className="font-black text-xl text-bark">Billing Center</h4>
            <div className="flex items-center space-x-2">
               <span className="w-3 h-3 bg-gecko rounded-full animate-pulse" />
               <span className="text-xs font-bold text-stone-400">Live Sync to Sheets</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-8 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest">Project</th>
                  <th className="px-8 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest text-right">Quote</th>
                  <th className="px-8 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {pendingInvoices.map(job => (
                  <tr key={job.id} className="group hover:bg-stone-50 transition-colors">
                    <td className="px-8 py-6">
                      <p className="font-bold text-bark">{job.clientName}</p>
                      <p className="text-[10px] font-mono text-stone-400 uppercase mt-1">#GT-{job.id.substr(0,4)}</p>
                    </td>
                    <td className="px-8 py-6 font-black text-palm text-right text-lg">${job.totalPrice}</td>
                    <td className="px-8 py-6 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button 
                          onClick={() => setPreviewJob(job)}
                          className="p-2 text-stone-400 hover:text-palm transition-colors"
                          title="Preview Estimate/Invoice"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        </button>
                        {job.status === JobStatus.COMPLETED ? (
                          <button 
                            onClick={() => setConfirmingAction({ type: 'invoice', status: JobStatus.INVOICED, item: job })}
                            className="px-4 py-2 bg-palm text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-gecko transition-colors shadow-lg shadow-palm/10"
                          >
                            Send Invoice
                          </button>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-[10px] font-black uppercase text-stone-400 px-3 py-1 bg-stone-100 rounded-full tracking-widest">Invoiced</span>
                            <button 
                              onClick={() => alert(`Payment reminder sent to ${job.email}`)}
                              className="px-3 py-1.5 bg-stone-100 text-stone-500 hover:bg-stone-200 text-[10px] font-black uppercase tracking-widest rounded-xl transition-colors"
                              title="Send Payment Reminder via Gmail"
                            >
                              Remind
                            </button>
                            <button 
                              onClick={() => setConfirmingAction({ type: 'invoice_paid', status: JobStatus.PAID, item: job })}
                              className="px-3 py-1.5 bg-gecko text-white hover:bg-gecko/90 text-[10px] font-black uppercase tracking-widest rounded-xl transition-colors"
                              title="Mark as Paid"
                            >
                              Paid
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {pendingInvoices.length === 0 && (
                  <tr><td colSpan={3} className="px-8 py-20 text-center text-stone-400 font-bold italic">Queue clear. All completed jobs invoiced.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Expense Intelligence */}
        <div className="bg-white rounded-[40px] border border-stone-200 overflow-hidden shadow-sm">
          <div className="p-8 border-b border-stone-100 flex justify-between items-center bg-stone-50">
            <h4 className="font-black text-xl text-bark">Expense Intelligence</h4>
            <div className="flex space-x-3">
              <input type="file" className="hidden" ref={receiptInputRef} accept="image/*" onChange={handleReceiptUpload} />
              <button 
                disabled={isScanning}
                onClick={() => receiptInputRef.current?.click()}
                className="text-[10px] font-black bg-stone-200 text-bark px-4 py-3 rounded-2xl hover:bg-stone-300 transition uppercase tracking-widest flex items-center space-x-2"
              >
                {isScanning ? (
                  <span>AI Scanning...</span>
                ) : (
                  <>
                    <Icons.Finance />
                    <span>Scan Receipt</span>
                  </>
                )}
              </button>
              <button 
                onClick={handleManualAdd}
                className="text-[10px] font-black bg-bark text-white px-4 py-3 rounded-2xl hover:bg-stone-800 transition shadow-xl shadow-bark/20 uppercase tracking-widest"
              >
                + Manual Entry
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-stone-50/50">
                <tr>
                  <th className="px-8 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest">Vendor</th>
                  <th className="px-8 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest">Account</th>
                  <th className="px-8 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest text-right">Debit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {expenses.map(expense => (
                  <tr key={expense.id} className="hover:bg-red-50/30 transition-colors">
                    <td className="px-8 py-6">
                      <p className="font-bold text-bark">{expense.merchant}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-[10px] text-stone-400 font-bold">{new Date(expense.date).toLocaleDateString()}</p>
                        {expense.receiptUrl && (
                          <a 
                            href={expense.receiptUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[10px] text-palm font-bold hover:underline flex items-center"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path></svg>
                            Receipt
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <span className="bg-stone-100 px-3 py-1 rounded-full text-[9px] font-black uppercase text-stone-500 tracking-widest border border-stone-200">{expense.category}</span>
                    </td>
                    <td className="px-8 py-6 font-black text-red-500 text-right text-lg">-${(Number(expense.amount) || 0).toLocaleString()}</td>
                  </tr>
                ))}
                {expenses.length === 0 && (
                  <tr><td colSpan={3} className="px-8 py-20 text-center text-stone-400 font-bold italic">No expenses recorded for this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
        </div>
      )}

      {activeTab === 'ledger' && (
        <div className="space-y-8 animate-fade-in">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="bg-bark text-white rounded-3xl p-5">
              <p className="text-[9px] font-black uppercase tracking-widest text-stone-300">Running balance</p>
              <p className={`text-2xl font-black mt-1 ${ledgerRunning.balance >= 0 ? 'text-leaf' : 'text-red-300'}`}>
                ${ledgerRunning.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-[9px] font-bold text-stone-400 mt-1">All posted ledger entries</p>
            </div>
            <div className="bg-white rounded-3xl border border-stone-200 p-5">
              <p className="text-[9px] font-black uppercase tracking-widest text-stone-400">Income</p>
              <p className="text-2xl font-black text-gecko mt-1">${ledgerRunning.income.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-3xl border border-stone-200 p-5">
              <p className="text-[9px] font-black uppercase tracking-widest text-stone-400">Expenses</p>
              <p className="text-2xl font-black text-red-500 mt-1">${ledgerRunning.expense.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-3xl border border-stone-200 p-5">
              <p className="text-[9px] font-black uppercase tracking-widest text-stone-400">Unreconciled</p>
              <p className="text-2xl font-black text-amber-600 mt-1">{ledgerRunning.unreconciled}</p>
            </div>
            <div className="bg-white rounded-3xl border border-stone-200 p-5 col-span-2 lg:col-span-1">
              <p className="text-[9px] font-black uppercase tracking-widest text-stone-400">Last ledger sync</p>
              <p className="text-sm font-black text-bark mt-1">
                {ledgerSync?.lastSyncAt ? new Date(ledgerSync.lastSyncAt).toLocaleString() : 'Not recorded'}
              </p>
              <p className="text-[9px] font-bold text-stone-400 mt-1">
                {ledgerSync?.entriesAdded != null ? `${ledgerSync.entriesAdded} entries added` : 'Daily sync expected'}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-[40px] border border-stone-200 overflow-hidden shadow-sm">
            <div className="p-8 border-b border-stone-100 flex justify-between items-center bg-stone-50">
              <div>
                <h4 className="font-black text-xl text-bark">General Ledger</h4>
                <p className="text-xs text-stone-500 font-bold uppercase tracking-widest mt-1">
                  Running tally · {visibleLedger.length} posted entries
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-end">
                <button
                  onClick={syncLedgerNow}
                  disabled={isSyncingLedger}
                  className="text-[10px] font-black bg-white border border-stone-200 text-stone-700 px-4 py-3 rounded-2xl hover:bg-stone-100 transition uppercase tracking-widest disabled:opacity-50"
                >
                  {isSyncingLedger ? 'Syncing…' : 'Sync Gmail'}
                </button>
                <button 
                  onClick={() => {
                    setCurrentLedgerEntry({
                      transactionId: `GT-${new Date().toISOString().split('T')[0].replace(/-/g, '')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
                      date: new Date().toISOString().split('T')[0],
                      type: 'Expense',
                      description: '',
                      payerPayee: '',
                      category: 'Operating Expense',
                      amount: 0,
                      paymentMethod: 'Credit Card',
                      reconciled: false
                    });
                    setShowLedgerModal(true);
                  }}
                  className="text-[10px] font-black bg-bark text-white px-6 py-3 rounded-2xl hover:bg-stone-800 transition shadow-xl shadow-bark/20 uppercase tracking-widest"
                >
                  + New Transaction
                </button>
              </div>
            </div>
            {ledgerSyncMessage && (
              <div className="px-8 py-3 text-xs font-bold text-stone-600 bg-stone-50 border-b border-stone-100">{ledgerSyncMessage}</div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-6 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest">ID / Date</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest">Type</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest">Description</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest">Payer/Payee</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest">Category</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest text-right">Amount</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest text-right">Running Balance</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest text-center">Method</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase text-stone-400 tracking-widest text-center">Reconciled</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {visibleLedger.sort((a, b) => b.date.localeCompare(a.date)).map(entry => (
                    <tr 
                      key={entry.id} 
                      className="hover:bg-stone-50 transition-colors cursor-pointer"
                      onClick={() => {
                        setCurrentLedgerEntry({
                          ...entry,
                          date: entry.date.split('T')[0]
                        });
                        setShowLedgerModal(true);
                      }}
                    >
                      <td className="px-6 py-6">
                        <p className="font-mono text-[10px] text-stone-400">{entry.transactionId}</p>
                        <p className="font-bold text-bark mt-1">{new Date(entry.date).toLocaleDateString()}</p>
                      </td>
                      <td className="px-6 py-6">
                        <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
                          entry.type === 'Income' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {entry.type}
                        </span>
                      </td>
                      <td className="px-6 py-6 font-medium text-bark">{entry.description}</td>
                      <td className="px-6 py-6 font-bold text-stone-600">{entry.payerPayee}</td>
                      <td className="px-6 py-6">
                        <span className="bg-stone-100 px-3 py-1 rounded-full text-[9px] font-black uppercase text-stone-500 tracking-widest border border-stone-200">
                          {entry.category}
                        </span>
                      </td>
                      <td className={`px-6 py-6 font-black text-right text-lg ${entry.type === 'Income' ? 'text-gecko' : 'text-red-500'}`}>
                        {entry.type === 'Income' ? '+' : '-'}${(Number(entry.amount) || 0).toLocaleString()}
                      </td>
                      <td className={`px-6 py-6 font-black text-right ${((ledgerRunning.byId.get(entry.id) || 0) >= 0) ? 'text-bark' : 'text-red-500'}`}>
                        ${(ledgerRunning.byId.get(entry.id) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-6 text-center text-[10px] font-bold text-stone-400 uppercase tracking-widest">{entry.paymentMethod}</td>
                      <td className="px-6 py-6 text-center">
                        {entry.reconciled ? (
                          <span className="text-gecko font-black">TRUE</span>
                        ) : (
                          <span className="text-red-400 font-black">FALSE</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {visibleLedger.length === 0 && (
                    <tr><td colSpan={9} className="px-8 py-20 text-center text-stone-400 font-bold italic">No ledger entries found. Start by adding a new transaction or running Gmail sync.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'budgeting' && (
        <div className="space-y-8 animate-fade-in">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Income vs Expenses Chart (Placeholder) */}
            <div className="bg-white rounded-[40px] border border-stone-200 p-8 shadow-sm">
              <h4 className="font-black text-xl text-bark mb-6">Income vs. Expenses (YTD)</h4>
              <div className="h-64 flex items-end space-x-4">
                {/* Mock Chart Bars */}
                <div className="w-1/2 bg-stone-100 rounded-t-xl h-full relative group">
                  <div className="absolute bottom-0 w-full bg-gecko rounded-t-xl h-[80%] transition-all duration-500 group-hover:opacity-80"></div>
                  <div className="absolute -top-8 w-full text-center text-sm font-bold text-stone-500">Income</div>
                </div>
                <div className="w-1/2 bg-stone-100 rounded-t-xl h-full relative group">
                  <div className="absolute bottom-0 w-full bg-red-400 rounded-t-xl h-[45%] transition-all duration-500 group-hover:opacity-80"></div>
                  <div className="absolute -top-8 w-full text-center text-sm font-bold text-stone-500">Expenses</div>
                </div>
              </div>
            </div>

            {/* Expense Breakdown */}
            <div className="bg-white rounded-[40px] border border-stone-200 p-8 shadow-sm">
              <h4 className="font-black text-xl text-bark mb-6">Expense Breakdown</h4>
              <div className="space-y-4">
                {/* Mock Categories */}
                {['Labor', 'Materials', 'Fuel', 'Insurance', 'Marketing'].map((category, index) => (
                  <div key={category}>
                    <div className="flex justify-between text-sm font-bold mb-1">
                      <span className="text-stone-500">{category}</span>
                      <span className="text-bark">{[45, 30, 10, 10, 5][index]}%</span>
                    </div>
                    <div className="w-full bg-stone-100 rounded-full h-2">
                      <div className="bg-palm h-2 rounded-full" style={{ width: `${[45, 30, 10, 10, 5][index]}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'taxes' && (
        <div className="space-y-8 animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
              <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Est. Federal Tax</p>
              <h3 className="text-4xl font-black text-bark mt-2">$12,450</h3>
              <p className="text-xs text-stone-500 mt-2">Q1 Estimated Payment</p>
            </div>
            <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
              <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Est. State Tax</p>
              <h3 className="text-4xl font-black text-bark mt-2">$4,120</h3>
              <p className="text-xs text-stone-500 mt-2">Q1 Estimated Payment</p>
            </div>
            <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
              <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Payroll Liabilities</p>
              <h3 className="text-4xl font-black text-bark mt-2">$8,900</h3>
              <p className="text-xs text-stone-500 mt-2">Due next period</p>
            </div>
          </div>

          <div className="bg-white rounded-[40px] border border-stone-200 overflow-hidden shadow-sm">
            <div className="p-8 border-b border-stone-100 flex justify-between items-center bg-stone-50">
              <h4 className="font-black text-xl text-bark">Tax Documents & Reports</h4>
            </div>
            <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button className="p-6 border-2 border-dashed border-stone-200 rounded-2xl flex flex-col items-center justify-center text-stone-500 hover:text-palm hover:border-palm transition-colors group">
                <svg className="w-8 h-8 mb-2 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                <span className="font-bold text-sm">Profit & Loss (YTD)</span>
              </button>
              <button className="p-6 border-2 border-dashed border-stone-200 rounded-2xl flex flex-col items-center justify-center text-stone-500 hover:text-palm hover:border-palm transition-colors group">
                <svg className="w-8 h-8 mb-2 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                <span className="font-bold text-sm">Balance Sheet</span>
              </button>
              <button className="p-6 border-2 border-dashed border-stone-200 rounded-2xl flex flex-col items-center justify-center text-stone-500 hover:text-palm hover:border-palm transition-colors group">
                <svg className="w-8 h-8 mb-2 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                <span className="font-bold text-sm">W-2s (Employees)</span>
              </button>
              <button className="p-6 border-2 border-dashed border-stone-200 rounded-2xl flex flex-col items-center justify-center text-stone-500 hover:text-palm hover:border-palm transition-colors group">
                <svg className="w-8 h-8 mb-2 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                <span className="font-bold text-sm">1099s (Contractors)</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'approvals' && (
        <div className="space-y-8 animate-fade-in">
          {/* Time Entries Approval */}
          <div className="bg-white rounded-[40px] border border-stone-200 overflow-hidden shadow-sm">
            <div className="p-8 border-b border-stone-100 flex justify-between items-center bg-stone-50">
              <h4 className="font-black text-xl text-bark">Employee Time Entries</h4>
              <span className="bg-stone-200 text-stone-600 text-xs font-bold px-3 py-1 rounded-full">
                {pendingTimeEntries.length} Pending
              </span>
            </div>
            <div className="p-8">
              {pendingTimeEntries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center text-stone-400 mb-4">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                  </div>
                  <h5 className="text-lg font-bold text-bark mb-1">All Caught Up!</h5>
                  <p className="text-stone-500 text-sm max-w-sm">There are no pending employee time entries requiring your approval at this time.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingTimeEntries.map(entry => (
                    <div key={entry.id} className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 bg-stone-50 rounded-2xl border border-stone-100 gap-4">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-bold text-bark">{entry.userName}</span>
                          <span className="text-stone-400 text-sm">•</span>
                          <span className="text-stone-500 text-sm">{new Date(entry.date).toLocaleDateString()}</span>
                        </div>
                        <p className="text-sm text-stone-600 mt-1">{entry.description}</p>
                        <p className="text-gecko font-bold mt-1">{entry.hours} hours</p>
                      </div>
                      <div className="flex space-x-2 w-full md:w-auto">
                        <button 
                          onClick={() => setConfirmingAction({ type: 'time_entry', status: 'APPROVED', item: entry })}
                          className="flex-1 md:flex-none px-4 py-2 bg-gecko text-white rounded-xl font-bold text-sm hover:bg-gecko/90 transition"
                        >
                          Approve
                        </button>
                        <button 
                          onClick={() => setConfirmingAction({ type: 'time_entry', status: 'REJECTED', item: entry })}
                          className="flex-1 md:flex-none px-4 py-2 bg-red-50 text-red-600 rounded-xl font-bold text-sm hover:bg-red-100 transition"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Contractor Invoices Approval */}
          <div className="bg-white rounded-[40px] border border-stone-200 overflow-hidden shadow-sm">
            <div className="p-8 border-b border-stone-100 flex justify-between items-center bg-stone-50">
              <h4 className="font-black text-xl text-bark">Contractor Invoices</h4>
              <span className="bg-stone-200 text-stone-600 text-xs font-bold px-3 py-1 rounded-full">
                {pendingContractorInvoices.length} Pending
              </span>
            </div>
            <div className="p-8">
              {pendingContractorInvoices.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center text-stone-400 mb-4">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                  </div>
                  <h5 className="text-lg font-bold text-bark mb-1">Inbox Zero!</h5>
                  <p className="text-stone-500 text-sm max-w-sm">There are no pending contractor invoices requiring your approval at this time.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingContractorInvoices.map(invoice => (
                    <div key={invoice.id} className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 bg-stone-50 rounded-2xl border border-stone-100 gap-4">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-bold text-bark">{invoice.contractorName}</span>
                          <span className="text-stone-400 text-sm">•</span>
                          <span className="text-stone-500 text-sm">{new Date(invoice.date).toLocaleDateString()}</span>
                          {invoice.invoiceUrl && (
                            <>
                              <span className="text-stone-400 text-sm">•</span>
                              <a 
                                href={invoice.invoiceUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[10px] text-palm font-bold hover:underline flex items-center"
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path></svg>
                                View Invoice
                              </a>
                            </>
                          )}
                        </div>
                        <p className="text-sm text-stone-600 mt-1">{invoice.description}</p>
                        <p className="text-gecko font-bold mt-1">${invoice.amount.toLocaleString()}</p>
                      </div>
                      <div className="flex space-x-2 w-full md:w-auto">
                        <button 
                          onClick={() => setConfirmingAction({ type: 'contractor_invoice', status: 'APPROVED', item: invoice })}
                          className="flex-1 md:flex-none px-4 py-2 bg-gecko text-white rounded-xl font-bold text-sm hover:bg-gecko/90 transition"
                        >
                          Approve
                        </button>
                        <button 
                          onClick={() => setConfirmingAction({ type: 'contractor_invoice', status: 'REJECTED', item: invoice })}
                          className="flex-1 md:flex-none px-4 py-2 bg-red-50 text-red-600 rounded-xl font-bold text-sm hover:bg-red-100 transition"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Approved Invoices ready for payment */}
            {contractorInvoices.filter(i => i.status === 'APPROVED').length > 0 && (
              <div className="p-8 border-t border-stone-100">
                <h5 className="font-bold text-stone-500 uppercase tracking-widest text-xs mb-4">Ready for Payment</h5>
                <div className="space-y-4">
                  {contractorInvoices.filter(i => i.status === 'APPROVED').map(invoice => (
                    <div key={invoice.id} className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 bg-palm/5 rounded-2xl border border-palm/20 gap-4">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-bold text-bark">{invoice.contractorName}</span>
                          <span className="text-stone-400 text-sm">•</span>
                          <span className="text-stone-500 text-sm">{new Date(invoice.date).toLocaleDateString()}</span>
                          {invoice.invoiceUrl && (
                            <>
                              <span className="text-stone-400 text-sm">•</span>
                              <a 
                                href={invoice.invoiceUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[10px] text-palm font-bold hover:underline flex items-center"
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path></svg>
                                View Invoice
                              </a>
                            </>
                          )}
                        </div>
                        <p className="text-sm text-stone-600 mt-1">{invoice.description}</p>
                        <p className="text-gecko font-bold mt-1">${invoice.amount.toLocaleString()}</p>
                      </div>
                      <div className="flex space-x-2 w-full md:w-auto">
                        <button 
                          onClick={() => setConfirmingAction({ type: 'contractor_invoice', status: 'PAID', item: invoice })}
                          className="flex-1 md:flex-none px-4 py-2 bg-palm text-white rounded-xl font-bold text-sm hover:bg-palm/90 transition"
                        >
                          Mark as Paid
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-[32px] w-full max-w-md shadow-2xl p-8 animate-in fade-in zoom-in duration-300">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-bark">Log Expense</h3>
              <button onClick={() => setShowExpenseModal(false)} className="text-stone-400 hover:text-bark">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
            
            <form onSubmit={handleSaveExpense} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Merchant / Vendor</label>
                <input 
                  required
                  type="text"
                  placeholder="e.g. Home Depot"
                  className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-bold text-bark"
                  value={currentExpense.merchant}
                  onChange={e => setCurrentExpense({...currentExpense, merchant: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Amount ($)</label>
                  <input 
                    required
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-bold text-bark"
                    value={currentExpense.amount}
                    onChange={e => setCurrentExpense({...currentExpense, amount: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Date</label>
                  <input 
                    required
                    type="date"
                    className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-bold text-bark"
                    value={currentExpense.date}
                    onChange={e => setCurrentExpense({...currentExpense, date: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Category</label>
                <select 
                  className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-bold text-bark appearance-none"
                  value={currentExpense.category}
                  onChange={e => setCurrentExpense({...currentExpense, category: e.target.value})}
                >
                  <option>Supplies</option>
                  <option>Fuel</option>
                  <option>Equipment</option>
                  <option>Labor</option>
                  <option>Insurance</option>
                  <option>Marketing</option>
                  <option>General</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Notes</label>
                <textarea 
                  rows={2}
                  className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko text-sm text-bark resize-none"
                  value={currentExpense.notes}
                  onChange={e => setCurrentExpense({...currentExpense, notes: e.target.value})}
                />
              </div>

              {currentExpense.receiptUrl ? (
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Receipt Image</label>
                  <div className="relative rounded-xl overflow-hidden border border-stone-200 bg-stone-50 h-32 flex items-center justify-center">
                    <img src={currentExpense.receiptUrl} alt="Receipt Preview" className="h-full object-contain" />
                    <button 
                      type="button"
                      onClick={() => setCurrentExpense({...currentExpense, receiptUrl: undefined})}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm shadow-md hover:bg-red-600 transition"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Receipt Image (Optional)</label>
                  <div className="w-full p-4 bg-stone-50 rounded-xl border border-stone-200 border-dashed flex flex-col items-center justify-center text-center">
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={(e) => {
                        if (!e.target.files?.[0]) return;
                        const reader = new FileReader();
                        reader.onloadend = () => setCurrentExpense({...currentExpense, receiptUrl: reader.result as string});
                        reader.readAsDataURL(e.target.files[0]);
                      }}
                      className="hidden"
                      id="manual-receipt-upload"
                    />
                    <label 
                      htmlFor="manual-receipt-upload"
                      className="cursor-pointer text-gecko font-bold hover:text-palm transition"
                    >
                      Click to browse
                    </label>
                  </div>
                </div>
              )}

              <div className="pt-2">
                <button 
                  type="submit"
                  className="w-full py-4 bg-palm text-white rounded-xl font-bold shadow-xl shadow-palm/20 hover:bg-bark transition"
                >
                  Save Expense Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ledger Transaction Modal */}
      {showLedgerModal && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-[32px] w-full max-w-2xl shadow-2xl p-8 animate-in fade-in zoom-in duration-300 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-bark">{currentLedgerEntry.id ? 'Edit' : 'New'} Ledger Transaction</h3>
              <button onClick={() => setShowLedgerModal(false)} className="text-stone-400 hover:text-bark">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
            
            <form onSubmit={handleSaveLedgerEntry} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Transaction ID</label>
                  <input 
                    required
                    type="text"
                    className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-mono text-sm text-bark"
                    value={currentLedgerEntry.transactionId}
                    onChange={e => setCurrentLedgerEntry({...currentLedgerEntry, transactionId: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Date</label>
                  <input 
                    required
                    type="date"
                    className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-bold text-bark"
                    value={currentLedgerEntry.date}
                    onChange={e => setCurrentLedgerEntry({...currentLedgerEntry, date: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Transaction Type</label>
                  <select 
                    className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-bold text-bark appearance-none"
                    value={currentLedgerEntry.type}
                    onChange={e => setCurrentLedgerEntry({...currentLedgerEntry, type: e.target.value as any})}
                  >
                    <option value="Income">Income</option>
                    <option value="Expense">Expense</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Amount ($)</label>
                  <input 
                    required
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-bold text-bark"
                    value={currentLedgerEntry.amount}
                    onChange={e => setCurrentLedgerEntry({...currentLedgerEntry, amount: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Description</label>
                <input 
                  required
                  type="text"
                  placeholder="e.g. Tree Removal - 123 Maple St"
                  className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-bold text-bark"
                  value={currentLedgerEntry.description}
                  onChange={e => setCurrentLedgerEntry({...currentLedgerEntry, description: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Payer / Payee</label>
                  <input 
                    required
                    type="text"
                    placeholder="e.g. John Doe or Home Depot"
                    className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-bold text-bark"
                    value={currentLedgerEntry.payerPayee}
                    onChange={e => setCurrentLedgerEntry({...currentLedgerEntry, payerPayee: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Category</label>
                  <select 
                    className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-bold text-bark appearance-none"
                    value={currentLedgerEntry.category}
                    onChange={e => setCurrentLedgerEntry({...currentLedgerEntry, category: e.target.value})}
                  >
                    <option>Service Income</option>
                    <option>Consultation Fee</option>
                    <option>Operating Expense</option>
                    <option>Supplies</option>
                    <option>Labor</option>
                    <option>Equipment</option>
                    <option>Fuel</option>
                    <option>Insurance</option>
                    <option>Marketing</option>
                    <option>General</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Payment Method</label>
                  <select 
                    className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko font-bold text-bark appearance-none"
                    value={currentLedgerEntry.paymentMethod}
                    onChange={e => setCurrentLedgerEntry({...currentLedgerEntry, paymentMethod: e.target.value})}
                  >
                    <option>Credit Card</option>
                    <option>Check</option>
                    <option>ACH / Wire</option>
                    <option>Cash</option>
                    <option>Venmo/Zelle</option>
                  </select>
                </div>
                <div className="flex items-center space-x-3 pt-6">
                  <input 
                    type="checkbox"
                    id="reconciled"
                    className="w-6 h-6 rounded-lg text-palm focus:ring-palm border-stone-300"
                    checked={currentLedgerEntry.reconciled}
                    onChange={e => setCurrentLedgerEntry({...currentLedgerEntry, reconciled: e.target.checked})}
                  />
                  <label htmlFor="reconciled" className="text-sm font-bold text-bark cursor-pointer uppercase tracking-widest">Reconciled</label>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Reference / Invoice URL (Optional)</label>
                <input 
                  type="url"
                  placeholder="https://..."
                  className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-gecko text-sm text-bark"
                  value={currentLedgerEntry.referenceUrl || ''}
                  onChange={e => setCurrentLedgerEntry({...currentLedgerEntry, referenceUrl: e.target.value})}
                />
              </div>

              <div className="pt-4 flex space-x-3">
                <button 
                  type="button"
                  onClick={() => setShowLedgerModal(false)}
                  className="flex-1 py-4 border-2 border-stone-100 text-stone-500 font-bold rounded-2xl hover:bg-stone-50 transition"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 py-4 bg-palm text-white rounded-xl font-bold shadow-xl shadow-palm/20 hover:bg-bark transition"
                >
                  {currentLedgerEntry.id ? 'Update' : 'Save'} Transaction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewJob && (
        <div className="fixed inset-0 bg-bark/80 backdrop-blur-md flex items-center justify-center z-[200] p-4 overflow-y-auto">
          <div className="relative w-full max-w-5xl my-8">
            <button 
              onClick={() => setPreviewJob(null)}
              className="absolute -top-12 right-0 text-white hover:text-gold transition-colors flex items-center space-x-2 font-black uppercase tracking-widest text-xs"
            >
              <span>Close Preview</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <div className="bg-white rounded-sm shadow-2xl overflow-hidden">
              <div className="p-4 bg-stone-100 border-b border-stone-200 flex justify-between items-center print:hidden">
                <div className="flex items-center space-x-4">
                  <span className="text-[10px] font-black uppercase tracking-widest text-stone-500">Document Preview</span>
                  <div className="h-4 w-px bg-stone-300"></div>
                  <span className="text-xs font-bold text-bark">{previewJob.clientName} - #GT-{previewJob.id.substring(0,8)}</span>
                </div>
                <button 
                  onClick={() => window.print()}
                  className="px-4 py-2 bg-bark text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-stone-800 transition shadow-lg"
                >
                  Print / Save PDF
                </button>
              </div>
              <div className="max-h-[80vh] overflow-y-auto bg-stone-200 p-8">
                <EstimateTemplate 
                  job={previewJob} 
                  type={previewJob.status === JobStatus.INVOICED || previewJob.status === JobStatus.PAID ? 'INVOICE' : 'ESTIMATE'} 
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmingAction && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[150] p-4">
          <div className="bg-white rounded-[32px] w-full max-w-md shadow-2xl p-8 text-center animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-bark mb-2">Confirm Financial Action</h3>
            <p className="text-stone-500 mb-8 leading-relaxed">
              You are about to {confirmingAction.status.toLowerCase()} this {confirmingAction.type.replace('_', ' ')}. 
              This will update the general ledger and potentially trigger payments.
            </p>
            <div className="flex space-x-3">
              <button 
                onClick={() => setConfirmingAction(null)}
                className="flex-1 py-4 border-2 border-stone-100 text-stone-500 font-bold rounded-2xl hover:bg-stone-50 transition"
              >
                Cancel
              </button>
              <button 
                onClick={handleConfirmAction}
                className="flex-1 py-4 bg-palm text-white font-bold rounded-2xl hover:bg-bark transition shadow-xl shadow-palm/20"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Financial Record Modal */}
      {editingRecord && onUpdateFinancialRecord && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[160] p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="bg-palm p-6 text-white">
              <h3 className="text-xl font-bold">Edit {editingRecord.type === 'INVOICE' ? 'Invoice' : 'Quote'} #GT-{editingRecord.id.slice(0, 4)}</h3>
              <p className="text-palm-100 text-sm opacity-80 italic">Changes are saved to Firestore and logged.</p>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); onUpdateFinancialRecord(editingRecord); setEditingRecord(null); }} className="p-8 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Type</label>
                  <select value={editingRecord.type} onChange={e => setEditingRecord({ ...editingRecord, type: e.target.value as any })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none">
                    <option value="ESTIMATE">ESTIMATE</option>
                    <option value="INVOICE">INVOICE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Status</label>
                  <select value={editingRecord.status} onChange={e => setEditingRecord({ ...editingRecord, status: e.target.value as any })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none">
                    {['DRAFT', 'SENT', 'PAID', 'OVERDUE'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Amount $</label>
                  <input type="number" min={0} step="0.01" value={editingRecord.amount} onChange={e => setEditingRecord({ ...editingRecord, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Due Date</label>
                  <input type="date" value={editingRecord.dueDate ? editingRecord.dueDate.slice(0, 10) : ''} onChange={e => setEditingRecord({ ...editingRecord, dueDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
                </div>
              </div>
              <div className="flex space-x-3 pt-2">
                <button type="button" onClick={() => setEditingRecord(null)} className="flex-1 py-3 text-stone-500 font-bold hover:bg-stone-100 rounded-lg transition">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-gecko text-white font-bold rounded-lg hover:bg-palm transition shadow-lg shadow-gecko/20">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Financial Record Modal */}
      {recordToDelete && onDeleteFinancialRecord && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[160] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h3 className="text-2xl font-bold text-bark mb-2">Delete this {recordToDelete.type === 'INVOICE' ? 'invoice' : 'quote'}?</h3>
            <p className="text-stone-500 mb-8 leading-relaxed">#GT-{recordToDelete.id.slice(0, 4)} (${Number(recordToDelete.amount || 0).toLocaleString()}) will be soft-deleted. The change log keeps a record of this action.</p>
            <div className="flex space-x-3">
              <button onClick={() => setRecordToDelete(null)} className="flex-1 py-3 border-2 border-stone-100 text-stone-500 font-bold rounded-xl hover:bg-stone-50 transition">Cancel</button>
              <button onClick={() => { onDeleteFinancialRecord(recordToDelete.id); setRecordToDelete(null); }} className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFinanceView;
